import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Divider,
  withStyles,
} from "@material-ui/core";
import LanguageSwitch from "../../../../Translations/LanguageSwitch";
import { TranslationRow } from "../../../../Translations/TranslationsRow";
import AITranslationButton from "../../../../Translations/AITranslationButton";
import {
  computeLanguageCompletion,
  sortTableData,
} from "../../../../Translations/utils/helpers";
import { buildLanguageBlob, findDuplicateOptionRowIds } from "../utils/translationBlob";
import { TranslationRowType } from "../utils/translationConstants";
import { translateWithAI } from "../services/diagnosticFlowsApiService";
import { DisplayMessage } from "../../../../../services/helpers";

const BorderLinearProgress = withStyles(() => ({
  root: { height: 8, borderRadius: 5 },
  bar: { borderRadius: 5 },
}))(LinearProgress);

export default function DiagnosticFlowTranslationsTable({
  rows,
  languages,
  defaultLang = "nl",
  resources,
  onSaveLanguage,
  hasTranslationLicence,
}) {
  const [selectedLang, setSelectedLang] = useState(defaultLang);
  const [tableData, setTableData] = useState(() =>
    sortTableData(rows, defaultLang)
  );
  // Rows are memoized and can keep a stale onSync, so syncs go through this ref
  // to always read the current table state.
  const tableDataRef = useRef(tableData);
  const [aiTranslating, setAiTranslating] = useState(false);

  const computedLanguages = useMemo(
    () => computeLanguageCompletion(languages, tableData),
    [languages, tableData]
  );

  const overallProgress = useMemo(() => {
    if (computedLanguages.length === 0) return 0;
    const total = computedLanguages.reduce((sum, l) => sum + l.percentage, 0);
    return Math.round(total / computedLanguages.length);
  }, [computedLanguages]);

  const totalKeys = useMemo(
    () =>
      tableData.reduce(
        (acc, row) => acc + (row.groupKeys ? row.groupKeys.length : 1),
        0
      ),
    [tableData]
  );

  const persist = useCallback(
    async (nextTableData, language) => {
      const blob = buildLanguageBlob(nextTableData, language);
      const ok = await onSaveLanguage(language, blob);
      return { success: !!ok };
    },
    [onSaveLanguage]
  );

  const applyAndPersist = useCallback(
    async (updatedRowData, language) => {
      const nextTableData = tableDataRef.current.map((row) =>
        row.id === updatedRowData.id ? updatedRowData : row
      );
      tableDataRef.current = nextTableData;
      setTableData(nextTableData);
      return persist(nextTableData, language);
    },
    [persist]
  );

  const handleSync = useCallback(
    (syncEvent, updatedRowData) => {
      if (updatedRowData.type === TranslationRowType.OPTION) {
        const candidate = tableDataRef.current.map((row) =>
          row.id === updatedRowData.id ? updatedRowData : row
        );
        if (findDuplicateOptionRowIds(candidate, syncEvent.language).has(updatedRowData.id)) {
          return Promise.resolve({ success: false, duplicate: true });
        }
      }
      return applyAndPersist(updatedRowData, syncEvent.language);
    },
    [applyAndPersist, resources]
  );

  const handleDelete = useCallback(
    (syncEvent, updatedRowData) =>
      applyAndPersist(updatedRowData, syncEvent.language),
    [applyAndPersist]
  );

  const allTranslationsFilled = useMemo(
    () =>
      tableData.every((row) => {
        const t = row.translations[selectedLang];
        return t && t.group && t.group.every((g) => g.value && g.value.trim().length > 0);
      }),
    [tableData, selectedLang]
  );

  const handleTranslateWithAI = async () => {
    setAiTranslating(true);
    try {
      const needRows = tableData.filter((row) => {
        const t = row.translations[selectedLang];
        return t && t.group && t.group.some((g) => !g.value || g.value.trim().length === 0);
      });
      if (needRows.length === 0) {
        setAiTranslating(false);
        return;
      }

      const request = {
        targetLanguages: [selectedLang],
        entities: needRows.map((row, i) => ({
          entityType: row.type,
          entityId: i,
          objects: row.groupKeys.map((key) => ({
            label: key,
            text: row.baseTranslations[key],
          })),
        })),
      };

      const aiResponse = await translateWithAI(request);

      const translatedByRowId = {};
      (aiResponse || []).forEach((entry) => {
        const row = needRows[entry.entityId];
        const translated = entry.translations && entry.translations[selectedLang];
        if (row && translated) translatedByRowId[row.id] = translated;
      });

      const updated = tableDataRef.current.map((row) => {
        const translated = translatedByRowId[row.id];
        if (!translated) return row;
        const current = row.translations[selectedLang];
        const group = current.group.map((g) => {
          const match = translated.find((x) => x.label === g.key);
          return match ? { ...g, value: match.text } : g;
        });
        return {
          ...row,
          translations: { ...row.translations, [selectedLang]: { ...current, group } },
        };
      });

      // Drop AI values that would make two options of one node read the same
      const duplicateIds = findDuplicateOptionRowIds(updated, selectedLang);
      const sanitized = updated.map((row) =>
        duplicateIds.has(row.id) && translatedByRowId[row.id]
          ? tableDataRef.current.find((r) => r.id === row.id)
          : row
      );

      tableDataRef.current = sanitized;
      setTableData(sanitized);
      await persist(sanitized, selectedLang);
      DisplayMessage(resources["aiTranslationSucceeded"], "success");
      if (duplicateIds.size > 0) {
        DisplayMessage(
          resources["DuplicateOptionTranslation"] ||
            "Options of the same question must have different translations",
          "warning"
        );
      }
    } catch (e) {
      DisplayMessage(resources["aiTranslationFailed"], "error");
    }
    setAiTranslating(false);
  };

  return (
    <>
      <Box mb={2}>
        <Box display="flex" alignItems="baseline">
          <Typography variant="h4" component="span" color="textPrimary">
            {overallProgress}%
          </Typography>
          <Typography
            variant="body2"
            component="span"
            color="textSecondary"
            style={{ margin: "0 8px" }}
          >
            {resources["OverallTranslated"]}
          </Typography>
          <Divider orientation="vertical" flexItem style={{ margin: "0 8px" }} />
          <Typography variant="h4" component="span" color="textPrimary">
            {totalKeys}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            color="textSecondary"
            style={{ margin: "0 8px" }}
          >
            {resources["TotalKeys"]}
          </Typography>
        </Box>
        <Box mt={1}>
          <BorderLinearProgress variant="determinate" value={overallProgress} />
        </Box>
      </Box>

      <Box mb="26px" display="flex">
        <LanguageSwitch
          languages={computedLanguages}
          selectedLang={selectedLang}
          onChange={setSelectedLang}
          id="diagnostic-flow-translations"
        />
        {hasTranslationLicence && (
          <AITranslationButton
            onClick={handleTranslateWithAI}
            buttonDisabled={aiTranslating || allTranslationsFilled}
            isTranslating={aiTranslating}
            allTranslationsFilled={allTranslationsFilled}
            resources={resources}
          />
        )}
      </Box>

      <Box display="flex" flexDirection="column">
        {tableData.map((rowData, index) => (
          <Box key={rowData.id} mb={index !== tableData.length - 1 ? 2 : 0}>
            <TranslationRow
              rowData={rowData}
              selectedLang={selectedLang}
              onSync={handleSync}
              onDelete={handleDelete}
              resources={resources}
            />
          </Box>
        ))}
      </Box>
    </>
  );
}
